import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Mail, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  XCircle,
  Send,
  Loader2,
  Shield,
  User
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Invite, User as UserType } from '../types/auth';
import { pushGA } from '../lib/utils';
import Badge from '../components/Badge';
import EmptyState from '../components/EmptyState';

export default function AdminPage() {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInvites = async () => {
    try {
      const { data, error } = await supabase
        .from('invites')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInvites(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar convites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar usuários:', err);
      setError(err.message);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
    fetchUsers();
    pushGA('page_view', { page_title: 'Admin - Convites' });
  }, []);

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setSending(true);
    setError(null);

    try {
      // Verificar convite pendente (antes de enviar outro)
      const { data: existing, error: existingErr } = await supabase
        .from('invites')
        .select('*')
        .eq('email', inviteEmail.trim())
        .eq('status', 'pending');

      if (existingErr) throw existingErr;
      if (existing && existing.length > 0) {
        throw new Error('Este email já possui um convite pendente');
      }

      // Enviar convite chamando a Edge Function (com JWT do usuário logado)
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Usuário não autenticado');

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ email: inviteEmail.trim() }),
        }
      );

      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'Falha ao enviar convite');
      }

      // Registra o convite na tabela
      const { error: insertError } = await supabase
        .from('invites')
        .insert({
          email: inviteEmail.trim(),
          status: 'pending',
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        });

      if (insertError) throw insertError;

      setInviteEmail('');
      await fetchInvites();
      
      pushGA('invite_sent', { email: inviteEmail.trim() });
      
    } catch (err: any) {
      console.error('Erro ao enviar convite:', err);
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteInvite = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este convite?')) return;

    try {
      const { error } = await supabase
        .from('invites')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      await fetchInvites();
      pushGA('invite_deleted', { invite_id: id });
      
    } catch (err: any) {
      console.error('Erro ao excluir convite:', err);
      setError(err.message);
    }
  };

  const getRoleBadge = (role: string) => {
    if (role === 'admin') {
      return (
        <Badge className="bg-darling-lilac/20 text-darling-lilac border-darling-lilac/30">
          <Shield className="h-3 w-3" />
          Admin
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-ocean-water/20 text-ocean-water border-ocean-water/30">
        <User className="h-3 w-3" />
        Embaixador
      </Badge>
    );
  };

  const getStatusBadge = (status: string, expiresAt: string) => {
    const isExpired = new Date(expiresAt) < new Date();
    
    if (status === 'accepted') {
      return (
        <Badge className="bg-ocean-water/20 text-ocean-water border-ocean-water/30">
          <CheckCircle className="h-3 w-3" />
          Aceito
        </Badge>
      );
    }
    
    if (status === 'pending' && isExpired) {
      return (
        <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
          <XCircle className="h-3 w-3" />
          Expirado
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30">
        <Clock className="h-3 w-3" />
        Pendente
      </Badge>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-darling-lilac/10 dark:bg-darling-lilac/20">
            <Users className="h-6 w-6 text-darling-lilac" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-exo font-black text-soft-black dark:text-white">
              Painel Administrativo
            </h1>
            <p className="text-soft-black/70 dark:text-light-gray/80 font-exo">
              Gerencie usuários e convites da plataforma
            </p>
          </div>
        </div>
      </div>

      {/* Lista de Usuários */}
      <div className="bg-white dark:bg-soft-black rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 shadow-lg overflow-hidden mb-8">
        <div className="p-6 border-b border-light-gray-alt dark:border-deep-purple/30">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-exo font-bold text-soft-black dark:text-white flex items-center gap-2">
              <Users className="h-5 w-5" />
              Usuários Cadastrados
            </h2>
            <button
              onClick={fetchUsers}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 hover:bg-light-gray dark:hover:bg-deep-purple/40 text-sm font-exo font-medium text-soft-black dark:text-white transition-all duration-200"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Atualizar
            </button>
          </div>
        </div>

        {usersLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 text-darling-lilac animate-spin mx-auto mb-4" />
            <p className="text-soft-black/70 dark:text-light-gray/80 font-exo">
              Carregando usuários...
            </p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-8">
            <EmptyState title="Nenhum usuário encontrado">
              Ainda não há usuários cadastrados no sistema.
            </EmptyState>
          </div>
        ) : (
          <div className="divide-y divide-light-gray-alt dark:divide-deep-purple/30">
            {users.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 hover:bg-light-gray/30 dark:hover:bg-deep-purple/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-exo font-semibold text-soft-black dark:text-white">
                        {user.email}
                      </h3>
                      {getRoleBadge(user.role)}
                      {user.must_set_password && (
                        <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30">
                          <Clock className="h-3 w-3" />
                          Aguardando senha
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-soft-black/60 dark:text-light-gray/70 font-exo">
                      <p>Cadastrado em: {new Date(user.created_at).toLocaleDateString('pt-BR')}</p>
                      {user.updated_at !== user.created_at && (
                        <p>Última atualização: {new Date(user.updated_at).toLocaleDateString('pt-BR')}</p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Formulário de Convite */}
      <div className="bg-white dark:bg-soft-black rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 p-6 mb-8 shadow-lg">
        <h2 className="text-lg font-exo font-bold text-soft-black dark:text-white mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Enviar Novo Convite
        </h2>
        
        <form onSubmit={handleSendInvite} className="flex gap-3">
          <div className="flex-1">
            <input
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@exemplo.com"
              className="w-full px-4 py-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black text-soft-black dark:text-white font-exo focus:ring-2 focus:ring-darling-lilac/20 focus:border-darling-lilac/50 transition-all"
            />
          </div>
          <button
            type="submit"
            disabled={sending || !inviteEmail.trim()}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-darling-lilac hover:bg-deep-purple disabled:bg-darling-lilac/50 text-white font-exo font-semibold transition-all duration-200 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Enviar Convite
              </>
            )}
          </button>
        </form>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50">
            <p className="text-sm text-red-600 dark:text-red-400 font-exo">
              {error}
            </p>
          </div>
        )}
      </div>

      {/* Lista de Convites */}
      <div className="bg-white dark:bg-soft-black rounded-2xl border border-light-gray-alt dark:border-deep-purple/50 shadow-lg overflow-hidden">
        <div className="p-6 border-b border-light-gray-alt dark:border-deep-purple/30">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-exo font-bold text-soft-black dark:text-white flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Convites Enviados
            </h2>
            <button
              onClick={fetchInvites}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 hover:bg-light-gray dark:hover:bg-deep-purple/40 text-sm font-exo font-medium text-soft-black dark:text-white transition-all duration-200"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Atualizar
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 text-darling-lilac animate-spin mx-auto mb-4" />
            <p className="text-soft-black/70 dark:text-light-gray/80 font-exo">
              Carregando convites...
            </p>
          </div>
        ) : invites.length === 0 ? (
          <div className="p-8">
            <EmptyState title="Nenhum convite enviado">
              Use o formulário acima para enviar o primeiro convite.
            </EmptyState>
          </div>
        ) : (
          <div className="divide-y divide-light-gray-alt dark:divide-deep-purple/30">
            {invites.map((invite) => (
              <motion.div
                key={invite.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 hover:bg-light-gray/30 dark:hover:bg-deep-purple/10 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-exo font-semibold text-soft-black dark:text-white">
                        {invite.email}
                      </h3>
                      {getStatusBadge(invite.status, invite.expires_at)}
                    </div>
                    <div className="text-sm text-soft-black/60 dark:text-light-gray/70 font-exo">
                      <p>Enviado em: {new Date(invite.created_at).toLocaleDateString('pt-BR')}</p>
                      <p>Expira em: {new Date(invite.expires_at).toLocaleDateString('pt-BR')}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleDeleteInvite(invite.id)}
                    className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors"
                    title="Excluir convite"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}